import { customElement } from "lit/decorators.js";
import { ShadowlessLitElement } from "@/components/meta/shadowless_lit_element";
import { I18n } from "@/i18n/i18n";
import {
    CARD_EASE,
    LAYOUT_SNAPSHOT_EVENT,
    type LayoutSnapshot,
    prefersReducedMotion,
    transitionLayout,
} from "@/motion";

type State = "accepted" | "regressed" | "started" | "unstarted";

/**
 * Sent on the document when the user's status of an activity changed: by the hand-in card once
 * a submission is judged, and when an activity is marked as read.
 */
export interface ActivityStatusChange {
    activityId: number;
    // The series the submission or read was saved in; null when it counts for no series.
    seriesId: number | null;
    state: State;
    // When the newest submission or the read was made, and whether it was accepted.
    at: string;
    accepted: boolean;
    submissionCount?: number;
    lastSubmissionUrl?: string;
    // For a read: its date as the server formats it.
    readDate?: string;
}

const STATES: State[] = ["accepted", "regressed", "started", "unstarted"];

function link(href: string, text: string): HTMLAnchorElement {
    const a = document.createElement("a");
    a.href = href;
    a.textContent = text;
    return a;
}

// The status words of an exercise, as in the submission history of the hand-in card.
function exerciseLabel(state: State): string {
    if (state === "accepted") return I18n.t("js.submission_history_solved");
    if (state === "regressed") return I18n.t("js.submission_history_wrong_after_correct");
    return I18n.t("js.submission_history_not_solved");
}

/**
 * Scrolls `list` so `item` is in view, to the nearest edge. Sets the scroll position of the list
 * itself, because scrollIntoView would also scroll the page.
 */
function scrollIntoList(list: HTMLElement, item: HTMLElement): void {
    const listBox = list.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    if (itemBox.bottom > listBox.bottom) {
        list.scrollTop += Math.ceil(itemBox.bottom - listBox.bottom);
    } else if (itemBox.top < listBox.top) {
        list.scrollTop -= Math.ceil(listBox.top - itemBox.top);
    }
}

/**
 * Wraps a server-rendered view of the series on the activity page: the panel in the left column, or
 * the strip above the title on a phone. Next to the panel it can hold the rail of status marks that
 * replaces the panel while a column stands next to the description: the open sandbox or the pinned
 * hand-in card.
 *
 * The rail has a button that shows the panel over the page, as an overlay that moves nothing. The
 * overlay closes on a click on that button, Escape, a click outside it, a click on an activity, when
 * focus moves to an element outside it and the button, or when the rail hides because that column
 * goes.
 * Focus moves to the open activity in the overlay without scrolling the page, and back to the button
 * when Escape or the button closes it. While the overlay is open, the marks of the rail are inert, so
 * the page has one list of the series. The button and Escape open and close the overlay in a view
 * transition of the page layout (`transitionLayout` of motion.ts), in which the marks move between
 * the rail and the panel.
 *
 * The panel scrolls its list so the open activity is in view in a long series. Browsers with
 * `scroll-initial-target` do this from the stylesheet; this is the fallback for the others,
 * with the same nearest-edge alignment. The overlay and the rail are hidden on load, so their lists
 * scroll to the open activity when they show: the overlay when it opens, the rail when it gets a
 * box (a ResizeObserver), as the sandbox opens or the hand-in card is pinned.
 *
 * On an `activity-status-changed` event saved in its series, it updates the activity's row, bar
 * segment and rail mark, wiping their new colour in, the solved count and the deadline pill. The marks and pills
 * come from the `template.activity-series-marks` and `template.activity-series-deadlines` the
 * server renders inside it, so they stay those of SegmentedBarComponent and DeadlinePillComponent.
 *
 * @element d-activity-series
 */
@customElement("d-activity-series")
export class ActivitySeries extends ShadowlessLitElement {
    private railObserver: ResizeObserver | null = null;
    private railShown = false;
    // The names of the status marks in view at the last snapshot of a layout transition.
    private marksInView = new Set<string>();

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener("activity-status-changed", this.onStatusChanged);
        document.addEventListener(LAYOUT_SNAPSHOT_EVENT, this.onLayoutSnapshot);
        this.toggleButton?.addEventListener("click", this.onToggleClick);
        const railList = this.railList;
        if (railList) {
            this.railObserver = new ResizeObserver(([entry]) => this.syncRail(railList, entry.contentRect.height > 0));
            this.railObserver.observe(railList);
        }
    }

    disconnectedCallback(): void {
        document.removeEventListener("activity-status-changed", this.onStatusChanged);
        document.removeEventListener(LAYOUT_SNAPSHOT_EVENT, this.onLayoutSnapshot);
        this.toggleButton?.removeEventListener("click", this.onToggleClick);
        this.railObserver?.disconnect();
        void this.closeOverlay(false);
        super.disconnectedCallback();
    }

    // The rail list has a box only while a column stands next to the description. An open overlay
    // closes with the rail: the close button of the sandbox, used from the keyboard, is no click
    // outside it. Focus stays where the sandbox puts it, as the toggle is hidden by then.
    private syncRail(list: HTMLElement, shown = list.getBoundingClientRect().height > 0): void {
        if (shown && !this.railShown) {
            const current = list.querySelector<HTMLElement>("[aria-current='page']");
            if (current) scrollIntoList(list, current);
        }
        if (!shown && this.railShown) void this.closeOverlay(false);
        this.railShown = shown;
    }

    /**
     * Before each snapshot of a layout transition, marks the status marks that move on their own
     * (`data-in-view`, activity_series.css.scss): those in view in their list, as the snapshot of one
     * scrolled out of its list would show over the panel. After the change, only those whose mark
     * was in view before too, so one without a place to come from stays in its list. The rail is
     * scrolled first, as it is when it shows.
     */
    private onLayoutSnapshot = (event: Event): void => {
        const snapshot = (event as CustomEvent<LayoutSnapshot>).detail;
        if (this.railList) this.syncRail(this.railList);
        const inViewBefore = this.marksInView;
        this.marksInView = new Set();
        for (const list of this.querySelectorAll<HTMLElement>(".activity-series-list, .activity-series-rail-list")) {
            const box = list.getBoundingClientRect();
            for (const mark of list.querySelectorAll<HTMLElement>(".activity-series-mark")) {
                const markBox = mark.getBoundingClientRect();
                const name = getComputedStyle(mark).getPropertyValue("--series-mark").trim();
                // The marks of the rail are not named while the overlay shows them.
                const inView =
                    box.height > 0 && markBox.bottom > box.top && markBox.top < box.bottom && !mark.closest("[inert]");
                if (inView) this.marksInView.add(name);
                mark.toggleAttribute("data-in-view", inView && (snapshot === "old" || inViewBefore.has(name)));
            }
        }
    };

    private get railList(): HTMLElement | null {
        return this.querySelector<HTMLElement>(".activity-series-rail-list");
    }

    private get toggleButton(): HTMLButtonElement | null {
        return this.querySelector<HTMLButtonElement>(".activity-series-rail-toggle");
    }

    private get overlay(): HTMLElement | null {
        return this.querySelector<HTMLElement>(".activity-series--panel");
    }

    private get overlayOpen(): boolean {
        return this.toggleButton?.getAttribute("aria-expanded") === "true";
    }

    private onToggleClick = (): void => {
        if (this.overlayOpen) {
            void this.closeOverlay(true, true);
        } else {
            void this.openOverlay();
        }
    };

    /** Shows the panel over the page in a view transition, in which the marks move out of the rail. */
    private async openOverlay(): Promise<void> {
        const overlay = this.overlay;
        if (!overlay) return;

        const current = overlay.querySelector<HTMLElement>("[aria-current='page']");
        await transitionLayout(() => {
            overlay.classList.add("activity-series--overlay");
            this.toggleButton.setAttribute("aria-expanded", "true");
            this.querySelector<HTMLElement>(".activity-series-rail-nav").inert = true;
            const list = overlay.querySelector<HTMLElement>(".activity-series-list");
            if (current && list) scrollIntoList(list, current.closest("li") ?? current);
            document.addEventListener("keydown", this.onOverlayKeydown);
            document.addEventListener("pointerdown", this.onOverlayPointerdown);
            overlay.addEventListener("click", this.onOverlayClick);
            this.addEventListener("focusout", this.onOverlayFocusout);
        });
        (current ?? overlay.querySelector<HTMLElement>("a"))?.focus({ preventScroll: true });
    }

    /**
     * Hides the panel again, in a view transition when `animate` is set. A click outside the panel
     * closes it at once: the page takes no clicks during a view transition, so the click would not
     * reach what it was meant for.
     */
    private async closeOverlay(returnFocus: boolean, animate = false): Promise<void> {
        if (!this.overlayOpen) return;

        const close = (): void => {
            const overlay = this.overlay;
            overlay?.classList.remove("activity-series--overlay");
            overlay?.removeEventListener("click", this.onOverlayClick);
            this.toggleButton.setAttribute("aria-expanded", "false");
            this.querySelector<HTMLElement>(".activity-series-rail-nav").inert = false;
            document.removeEventListener("keydown", this.onOverlayKeydown);
            document.removeEventListener("pointerdown", this.onOverlayPointerdown);
            this.removeEventListener("focusout", this.onOverlayFocusout);
        };
        if (animate) {
            await transitionLayout(close);
        } else {
            close();
        }
        if (returnFocus) this.toggleButton.focus();
    }

    private onOverlayKeydown = (event: KeyboardEvent): void => {
        if (event.key !== "Escape") return;

        event.preventDefault();
        void this.closeOverlay(true, true);
    };

    private onOverlayPointerdown = (event: PointerEvent): void => {
        const target = event.target as Node;
        if (this.overlay?.contains(target) || this.toggleButton.contains(target)) return;

        void this.closeOverlay(false);
    };

    // Focus that moves to no element, as on a click on the overlay itself, leaves it open.
    private onOverlayFocusout = (event: FocusEvent): void => {
        const next = event.relatedTarget as Node | null;
        if (!next || this.overlay?.contains(next) || this.toggleButton.contains(next)) return;

        void this.closeOverlay(false);
    };

    private onOverlayClick = (event: MouseEvent): void => {
        if ((event.target as Element).closest("a")) void this.closeOverlay(false);
    };

    protected firstUpdated(): void {
        void this.markRevealed();
        this.scrollToActive();
    }

    /**
     * Sets `revealed` once the entrance of the bar and the marks has played, which ends it: a
     * series hidden and shown again, as next to the open sandbox or the pinned card, would otherwise play it again.
     */
    private async markRevealed(): Promise<void> {
        const entrance = this.getAnimations?.({ subtree: true }) ?? [];
        await Promise.allSettled(entrance.map((animation) => animation.finished));
        this.toggleAttribute("revealed", true);
    }

    private scrollToActive(): void {
        if (CSS.supports("scroll-initial-target", "nearest")) return;

        const list = this.querySelector<HTMLElement>(".activity-series--panel .activity-series-list");
        const active = list?.querySelector<HTMLElement>(".activity-series-item.active");
        if (list && active) scrollIntoList(list, active);
    }

    private onStatusChanged = (event: Event): void => {
        const change = (event as CustomEvent<ActivityStatusChange>).detail;
        if (this.dataset.seriesId !== String(change.seriesId)) return;

        const rows = [...this.querySelectorAll<HTMLElement>(".activity-series-item")];
        const index = rows.findIndex((row) => row.dataset.activityId === String(change.activityId));
        if (index < 0) return;

        const row = rows[index];
        const exercise = row.dataset.submissionsUrl !== undefined;
        const label = exercise ? exerciseLabel(change.state) : I18n.t("js.activity_status.read");
        const name = row.querySelector(".activity-series-name").textContent.trim();
        const title = `${name} · ${label}`;

        this.mark(row.querySelector(".activity-series-mark .segment"), change.state, "down");
        const segment = this.querySelectorAll<HTMLElement>(".activity-series-bar .segment")[index];
        this.mark(segment, change.state, "right");
        if (segment) segment.title = title;

        const railLink = this.querySelector<HTMLElement>(
            `.activity-series-rail-item[data-activity-id="${change.activityId}"] .activity-series-rail-link`,
        );
        if (railLink) {
            this.mark(railLink.querySelector(".segment"), change.state, "down");
            // initTooltips moved the title into a Bootstrap tooltip
            const tooltip = window.bootstrap?.Tooltip.getInstance(railLink);
            if (tooltip) {
                tooltip.setContent({ ".tooltip-inner": title });
            } else {
                railLink.title = title;
            }
            railLink.setAttribute("aria-label", title);
        }

        const status = row.querySelector<HTMLElement>(".activity-series-status");
        status.classList.remove("visually-hidden");
        if (exercise && change.lastSubmissionUrl) {
            const latest = link(change.lastSubmissionUrl, label);
            const hidden = document.createElement("span");
            hidden.className = "visually-hidden";
            hidden.textContent = `, ${I18n.t("js.activity_status.latest_submission_link")}`;
            latest.append(hidden);
            const all = link(
                row.dataset.submissionsUrl,
                I18n.t("js.submission_count", { smart_count: change.submissionCount }),
            );
            status.replaceChildren(latest, " · ", all);
        } else {
            status.textContent = label;
        }

        this.updateCount(rows);
        this.updateDeadline(rows, row, change);
    };

    /**
     * As ActivityStatus#accepted_before_deadline: the last submission or the read before the
     * deadline decides, so one made after it leaves the row as it was.
     */
    private updateDeadline(rows: HTMLElement[], row: HTMLElement, change: ActivityStatusChange): void {
        if (row.dataset.beforeDeadline === undefined) return;

        const deadline = this.dataset.deadline;
        if (!deadline || new Date(change.at) < new Date(deadline)) {
            row.dataset.beforeDeadline = String(change.accepted);
        }

        const met = rows.every((r) => r.dataset.beforeDeadline === "true");
        const template = this.querySelector<HTMLTemplateElement>("template.activity-series-deadlines");
        const source = template?.content.querySelector(`[data-met="${met}"] > .deadline-pill`);
        const pill = this.querySelector(".deadline-pill");
        if (source && pill) pill.replaceWith(source.cloneNode(true));
    }

    private mark(segment: HTMLElement | undefined, state: State, direction: "right" | "down"): void {
        const template = this.querySelector<HTMLTemplateElement>("template.activity-series-marks");
        const source = template?.content.querySelector(`.segment-${state}`);
        if (!segment || !source) return;

        segment.classList.remove(...STATES.map((s) => `segment-${s}`));
        segment.classList.add(`segment-${state}`);
        segment.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
        this.wipe(segment, direction);
    }

    /**
     * Colours the new state in as the page load does: a bar segment from the left and the mark of a
     * row from the top. Shows its glyph once the wipe has passed the middle.
     */
    private wipe(segment: HTMLElement, direction: "right" | "down"): void {
        if (prefersReducedMotion()) return;

        const from = direction === "right" ? "0 100%" : "100% 0";
        segment.animate({ backgroundSize: [from, "100% 100%"] }, { duration: 400, easing: CARD_EASE });
        for (const glyph of segment.children) {
            glyph.animate({ opacity: [0, 1] }, { delay: 160, duration: 240, fill: "backwards" });
        }
    }

    private updateCount(rows: HTMLElement[]): void {
        const accepted = rows.filter((row) => row.querySelector(".activity-series-mark .segment-accepted")).length;
        this.querySelector(".activity-series-count-short").textContent = `${accepted}/${rows.length}`;
        this.querySelector(".activity-series-count-label").textContent = I18n.t("js.activity_series.count", {
            accepted: accepted,
            total: rows.length,
        });
    }
}
